import type {
  CmmdAttachWorkSessionThreadParams,
  CmmdCreateWorkSessionParams,
  CmmdCreateWorkSessionResult,
  CmmdWorkSession,
} from "@cmmd-forge/contracts";

export interface TaskWorkSessionCheckpoint {
  readonly idempotencyKey: string;
  readonly threadId: string;
  readonly sessionId: string | null;
  readonly threadCreated: boolean;
}

export interface TaskWorkSessionTarget {
  readonly runtimeId: string;
  readonly forgeProjectLinkId: number;
  readonly environmentId: string;
  readonly projectId: string;
}

export type ExistingTaskConversation =
  | {
      readonly kind: "thread";
      readonly environmentId: string;
      readonly threadId: string;
    }
  | {
      readonly kind: "starting";
    };

export interface TaskWorkSessionApi {
  readonly createTaskWorkSession: (
    projectId: string,
    taskId: number,
    params: CmmdCreateWorkSessionParams,
  ) => Promise<CmmdCreateWorkSessionResult>;
  readonly updateWorkSessionStatus: (
    workSessionId: string,
    status: "failed",
  ) => Promise<CmmdWorkSession>;
  readonly attachWorkSessionThread: (
    workSessionId: string,
    params: CmmdAttachWorkSessionThreadParams,
  ) => Promise<unknown>;
}

export interface LaunchTaskWorkSessionInput {
  readonly api: TaskWorkSessionApi;
  readonly projectId: string;
  readonly taskId: number;
  readonly taskTitle: string;
  readonly target: TaskWorkSessionTarget;
  readonly checkpoint: TaskWorkSessionCheckpoint;
  readonly createThread: (input: {
    readonly environmentId: string;
    readonly projectId: string;
    readonly threadId: string;
  }) => Promise<void>;
  readonly createIdempotencyKey: () => string;
  readonly onCheckpoint: (checkpoint: TaskWorkSessionCheckpoint) => void;
}

export interface LaunchTaskWorkSessionResult {
  readonly sessionId: string;
  readonly threadId: string;
  readonly environmentId: string;
}

export type StartTaskWorkSessionResult =
  | {
      readonly kind: "resumed";
      readonly threadId: string;
      readonly environmentId: string;
    }
  | ({
      readonly kind: "created";
    } & LaunchTaskWorkSessionResult);

export class TaskConversationAlreadyStartingError extends Error {
  constructor() {
    super("This task is already starting in another Forge session.");
    this.name = "TaskConversationAlreadyStartingError";
  }
}

export async function startOrResumeTaskWorkSession(
  input: Omit<LaunchTaskWorkSessionInput, "target"> & {
    readonly target: TaskWorkSessionTarget | null;
    readonly resolveExistingConversation: () => Promise<ExistingTaskConversation | null>;
    readonly preflight: () => Promise<void>;
  },
): Promise<StartTaskWorkSessionResult> {
  const existing = await input.resolveExistingConversation();
  if (existing?.kind === "thread") {
    return {
      kind: "resumed",
      environmentId: existing.environmentId,
      threadId: existing.threadId,
    };
  }
  if (existing?.kind === "starting") {
    throw new TaskConversationAlreadyStartingError();
  }
  if (input.target === null) {
    throw new Error("Select an available Forge runtime before starting this task.");
  }
  if (!input.checkpoint.threadCreated) {
    await input.preflight();
  }
  return {
    kind: "created",
    ...(await launchTaskWorkSession({
      ...input,
      target: input.target,
    })),
  };
}

export async function launchTaskWorkSession(
  input: LaunchTaskWorkSessionInput,
): Promise<LaunchTaskWorkSessionResult> {
  let checkpoint = input.checkpoint;
  if (checkpoint.sessionId === null) {
    const created = await input.api.createTaskWorkSession(input.projectId, input.taskId, {
      runtimeId: input.target.runtimeId,
      forgeProjectLinkId: input.target.forgeProjectLinkId,
      idempotencyKey: checkpoint.idempotencyKey,
    });
    checkpoint = { ...checkpoint, sessionId: created.session.id };
    input.onCheckpoint(checkpoint);
  }

  if (!checkpoint.threadCreated) {
    try {
      await input.createThread({
        environmentId: input.target.environmentId,
        projectId: input.target.projectId,
        threadId: checkpoint.threadId,
      });
      checkpoint = { ...checkpoint, threadCreated: true };
      input.onCheckpoint(checkpoint);
    } catch (cause) {
      await failStartedSession(input.api, checkpoint.sessionId);
      const retryCheckpoint = {
        idempotencyKey: input.createIdempotencyKey(),
        threadId: checkpoint.threadId,
        sessionId: null,
        threadCreated: false,
      } satisfies TaskWorkSessionCheckpoint;
      input.onCheckpoint(retryCheckpoint);
      throw cause;
    }
  }

  const sessionId = requireSessionId(checkpoint);
  await input.api.attachWorkSessionThread(sessionId, {
    forgeThreadId: checkpoint.threadId,
    title: input.taskTitle,
    kind: "root",
  });

  return {
    sessionId,
    threadId: checkpoint.threadId,
    environmentId: input.target.environmentId,
  };
}

async function failStartedSession(
  api: TaskWorkSessionApi,
  sessionId: string | null,
): Promise<void> {
  if (sessionId === null) return;
  try {
    await api.updateWorkSessionStatus(sessionId, "failed");
  } catch {
    // Preserve the original thread-creation failure. A stale `starting` session
    // remains visible to CMMD reconciliation instead of hiding the root cause.
  }
}

function requireSessionId(checkpoint: TaskWorkSessionCheckpoint): string {
  if (checkpoint.sessionId === null) {
    throw new Error("CMMD did not return a work-session id.");
  }
  return checkpoint.sessionId;
}
